import { logger } from '@/utils/logger';
import type { WordApi, WordParagraph, WordRange, WordSyncContext } from './wordTypes';
/**
 * Office.js helpers for the Templates tab: export the live (possibly
 * unsaved) document as .docx bytes for stateless backend validation, and
 * load a materialized template .docx into the open document.
 */

const DOCX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

type OfficeAsyncResult<T> = {
  status: unknown;
  value: T;
  error?: { message?: string };
};

type OfficeFileSlice = {
  data: number[] | Uint8Array;
};

type OfficeFileHandle = {
  sliceCount: number;
  getSliceAsync(
    sliceIndex: number,
    callback: (result: OfficeAsyncResult<OfficeFileSlice>) => void
  ): void;
  closeAsync(callback?: (result: OfficeAsyncResult<unknown>) => void): void;
};

type GetFileAsync = (
  fileType: unknown,
  options: Record<string, never>,
  callback: (result: OfficeAsyncResult<OfficeFileHandle>) => void
) => void;

type OfficeHostApi = {
  AsyncResultStatus?: { Succeeded?: unknown };
  FileType?: { Compressed?: unknown };
  context?: {
    document?: {
      getFileAsync?: GetFileAsync;
    };
  };
};

type OfficeDocumentFileApi = {
  getFileAsync: GetFileAsync;
  succeededStatus: unknown;
  compressedFileType: unknown;
};

function getWordApi(): WordApi | null {
  return (window as unknown as { Word?: WordApi }).Word ?? null;
}

function requireWordApi(): WordApi {
  const wordApi = getWordApi();
  if (!wordApi) {
    throw new Error('Word document context is not available in this host.');
  }
  return wordApi;
}

function requireOfficeDocumentFileApi(): OfficeDocumentFileApi {
  const office = (window as unknown as { Office?: OfficeHostApi }).Office;
  const getFileAsync = office?.context?.document?.getFileAsync;
  const succeededStatus = office?.AsyncResultStatus?.Succeeded;
  const compressedFileType = office?.FileType?.Compressed;
  if (!getFileAsync || succeededStatus === undefined || compressedFileType === undefined) {
    throw new Error('The Office document file API is not available in this host.');
  }
  return { getFileAsync, succeededStatus, compressedFileType };
}

function getDocumentFile(api: OfficeDocumentFileApi): Promise<OfficeFileHandle> {
  return new Promise((resolve, reject) => {
    api.getFileAsync(api.compressedFileType, {}, result => {
      if (result.status === api.succeededStatus) {
        resolve(result.value);
      } else {
        reject(new Error(result.error?.message ?? 'Could not read the Word document file.'));
      }
    });
  });
}

function getFileSlice(
  api: OfficeDocumentFileApi,
  file: OfficeFileHandle,
  sliceIndex: number
): Promise<OfficeFileSlice> {
  return new Promise((resolve, reject) => {
    file.getSliceAsync(sliceIndex, result => {
      if (result.status === api.succeededStatus) {
        resolve(result.value);
      } else {
        reject(new Error(result.error?.message ?? 'Could not read the Word document file.'));
      }
    });
  });
}

/**
 * Export the open Word document — including unsaved edits — as a .docx File
 * suitable for a multipart upload.
 */
export async function exportCurrentDocumentAsDocx(fileName: string): Promise<File> {
  const api = requireOfficeDocumentFileApi();
  const file = await getDocumentFile(api);
  try {
    const parts: Uint8Array[] = [];
    for (let index = 0; index < file.sliceCount; index += 1) {
      const slice = await getFileSlice(api, file, index);
      parts.push(slice.data instanceof Uint8Array ? slice.data : new Uint8Array(slice.data));
    }
    return new File(parts as BlobPart[], fileName, { type: DOCX_MIME_TYPE });
  } finally {
    // The host keeps the file handle locked until it's closed; failing to
    // close blocks subsequent getFileAsync calls.
    file.closeAsync(() => undefined);
  }
}

/**
 * Replace the entire body of the open document with the given .docx
 * (base64-encoded). Used to load a template's materialized file into Word.
 */
export async function replaceDocumentFromDocxBase64(base64Docx: string): Promise<void> {
  const wordApi = requireWordApi();
  await wordApi.run(async context => {
    context.document.body.insertFileFromBase64(base64Docx, 'Replace');
    await context.sync();
  });
}

/**
 * Insert text at the current cursor position (replacing the selection if one
 * exists). Multi-line text becomes separate paragraphs — Word treats `\n` as
 * a paragraph break.
 */
export async function insertTextAtSelection(text: string): Promise<void> {
  const wordApi = requireWordApi();
  await wordApi.run(async context => {
    const selection = context.document.getSelection();
    selection.insertText(text, 'Replace');
    await context.sync();
  });
}

/**
 * Wrap the current non-empty selection between `prefix` and `suffix` — e.g.
 * `{% if … %}` before the selected block and `{% endif %}` after it, leaving
 * the selected content untouched.
 *
 * By default the tags go on their own paragraphs (block scope). Pass
 * `inline: true` to keep them on the same line as the selection — for a
 * condition inside a sentence.
 *
 * Returns false (and inserts nothing) when the selection is collapsed, so
 * callers can fall back to a plain at-cursor insert.
 */
export async function wrapSelectionWithBlock(
  prefix: string,
  suffix: string,
  options: { inline?: boolean } = {}
): Promise<boolean> {
  const wordApi = requireWordApi();
  const sep = options.inline ? '' : '\n';
  return wordApi.run(async context => {
    const selection = context.document.getSelection();
    selection.load('text');
    await context.sync();
    if (selection.text.trim().length === 0) return false;
    selection.insertText(`${prefix}${sep}`, 'Start');
    selection.insertText(`${sep}${suffix}`, 'End');
    await context.sync();
    return true;
  });
}

async function searchAllRanges(context: WordSyncContext, text: string): Promise<WordRange[]> {
  const results = context.document.body.search(text, {
    ignorePunct: false,
    ignoreSpace: true,
    matchCase: false,
    matchWholeWord: false,
  });
  results.load('items');
  await context.sync();
  // Word returns search matches in document order.
  return results.items;
}

/** Outcome of locating a field, so the caller can cycle and report progress. */
export interface FieldLocationResult {
  found: boolean;
  /** 0-based index of the occurrence that was selected. */
  index: number;
  /** Total number of occurrences found for the field. */
  total: number;
}

/**
 * Find the Jinja tag for a schema field in the open document and select it
 * (Word scrolls the selection into view). Tries tag-shaped matches first
 * (`{{ field`, `{% if field`, loop sources) so prose that merely mentions
 * the field name doesn't win; falls back to the bare name.
 *
 * When a field is used in several places, pass `occurrenceIndex` to pick
 * which match to select — it wraps, so callers can cycle through every
 * occurrence with repeated clicks. The returned `index`/`total` let the
 * caller advance the cursor and tell the user "2 of 3".
 */
export async function selectTemplateFieldInDocument(
  fieldPath: string,
  occurrenceIndex = 0
): Promise<FieldLocationResult> {
  const wordApi = requireWordApi();

  // Rows nested under a list render `parent[].child` paths, but inside the
  // document the loop variable's alias replaces the parent — the full path
  // never appears verbatim, so only the leaf name is searchable.
  const insideLoop = fieldPath.includes('[]');
  const cleanPath = fieldPath.replace(/\[\]/g, '');
  const leafName = cleanPath.split('.').pop() ?? cleanPath;
  const candidates = insideLoop
    ? [leafName]
    : [
        `{{ ${cleanPath}`,
        `{% if ${cleanPath}`,
        `{% elif ${cleanPath}`,
        `in ${cleanPath} %}`,
        cleanPath,
      ];

  return wordApi.run(async context => {
    for (const candidate of candidates) {
      // Cycle within the first candidate that matches; a field is normally
      // used in one tag shape, so this keeps occurrences predictable.
      const ranges = await searchAllRanges(context, candidate);
      if (ranges.length > 0) {
        const total = ranges.length;
        const index = ((occurrenceIndex % total) + total) % total;
        ranges[index].select();
        await context.sync();
        return { found: true, index, total };
      }
    }
    return { found: false, index: 0, total: 0 };
  });
}

/**
 * Search for a literal piece of text in the document and select the first
 * match (Word scrolls it into view). Used to jump to the exact tag named in
 * a validation error. Returns false when nothing matched.
 *
 * Word's search caps at 255 chars, so a long tag is trimmed to a prefix.
 */
export async function selectTextInDocument(text: string): Promise<boolean> {
  const needle = text.trim();
  if (!needle) return false;
  const wordApi = requireWordApi();
  return wordApi.run(async context => {
    const ranges = await searchAllRanges(
      context,
      needle.length > 200 ? needle.slice(0, 200) : needle
    );
    if (ranges.length > 0) {
      ranges[0].select();
      await context.sync();
      return true;
    }
    return false;
  });
}

// ── Data-returning readers for the chat's document tools ────────────────────
// Siblings of the select*-in-document helpers above, but they RETURN the data
// (text / paragraphs / matches) instead of selecting it — the chat's
// pull-on-demand document tools (search / outline / section / chunks) run these
// against the live document and resume the agent with the result.

/** A search hit plus its containing-paragraph text as a little context. */
export interface DocumentSearchMatch {
  text: string;
  context: string;
}

/**
 * Search the document for `query` and return up to `maxResults` matches, each
 * with its containing-paragraph text. Data-returning sibling of
 * {@link selectTextInDocument}. Word caps the search needle at ~255 chars.
 */
export async function searchDocumentRanges(
  query: string,
  maxResults = 20
): Promise<DocumentSearchMatch[]> {
  const needle = query.trim();
  if (!needle) return [];
  const wordApi = requireWordApi();
  return wordApi.run(async context => {
    const ranges = await searchAllRanges(
      context,
      needle.length > 200 ? needle.slice(0, 200) : needle
    );
    const limited = ranges.slice(0, Math.max(1, maxResults));
    for (const range of limited) {
      range.load('text');
      range.paragraphs.load('items/text');
    }
    await context.sync();
    return limited.map(range => ({
      text: range.text,
      context: range.paragraphs.items
        .map(paragraph => paragraph.text)
        .join(' ')
        .trim(),
    }));
  });
}

/** A paragraph with its style names — the basis for outline / section tools. */
export interface DocumentParagraph {
  text: string;
  style: string | null;
  styleBuiltIn: string | null;
}

/**
 * Read every paragraph with its style names. Falls back to text-only if the
 * host can't load styles (older Word). Used to build the document outline and
 * extract a section.
 */
export async function readParagraphsWithStyle(): Promise<DocumentParagraph[]> {
  const wordApi = requireWordApi();
  return wordApi.run(async context => {
    const paragraphs = context.document.body.paragraphs;
    try {
      paragraphs.load('items/text,items/style,items/styleBuiltIn');
      await context.sync();
      return paragraphs.items.map((paragraph: WordParagraph) => ({
        text: paragraph.text,
        style: paragraph.style || null,
        styleBuiltIn: paragraph.styleBuiltIn || null,
      }));
    } catch (error) {
      logger.warn('Falling back to text-only paragraph read:', error);
      paragraphs.load('items/text');
      await context.sync();
      return paragraphs.items.map((paragraph: WordParagraph) => ({
        text: paragraph.text,
        style: null,
        styleBuiltIn: null,
      }));
    }
  });
}

/** A single find/replace edit for the apply-edits document tool. */
export interface DocumentEdit {
  find: string;
  replace: string;
  /**
   * Accepted for backward-compat but no longer used: occurrences are now mapped
   * by document order (one edit per occurrence), not by this field.
   */
  occurrence?: number | 'all';
}

/** Outcome of an apply-edits run, reported back to the agent. */
export interface DocumentEditOutcome {
  applied: number;
  total: number;
  failures: { find: string; detail: string }[];
}

/**
 * Apply find/replace edits to the live document in place (Word search +
 * `insertText('Replace')`), so surrounding formatting/structure is preserved.
 * The write half of the document tools — used for whole-template conversion.
 *
 * The backend emits ONE edit per occurrence, in document order. We group the
 * replacements by `find` and, with a SINGLE search pass per find, map the i-th
 * occurrence to the i-th edit's replacement (replacing last→first so earlier
 * ranges keep their positions). This is NOT replace-all — that would break the
 * contract two ways:
 *  - repeated finds with different replacements (one `{/}` → `{% endfor %}`,
 *    another `{/}` → `{% endif %}`) would all collapse to the first, and
 *  - a find that is a substring of its replacement (`{ x }` ⊂ `{{ x }}`) would
 *    be re-wrapped on every pass → brace explosion.
 * One search pass per find (before any replacement) avoids both.
 */
export async function applyDocumentEdits(edits: DocumentEdit[]): Promise<DocumentEditOutcome> {
  const wordApi = requireWordApi();
  return wordApi.run(async context => {
    const failures: { find: string; detail: string }[] = [];

    // Group replacements by find, preserving document order within each find.
    const groups = new Map<string, string[]>();
    for (const edit of edits) {
      const needle = edit.find.trim();
      if (!needle) {
        failures.push({ find: edit.find, detail: 'empty find' });
        continue;
      }
      const existing = groups.get(needle);
      if (existing) {
        existing.push(edit.replace);
      } else {
        groups.set(needle, [edit.replace]);
      }
    }

    let applied = 0;
    for (const [needle, replacements] of groups) {
      const ranges = await searchAllRanges(
        context,
        needle.length > 200 ? needle.slice(0, 200) : needle
      );
      if (ranges.length === 0) {
        failures.push({ find: needle, detail: 'not found' });
        continue;
      }
      // Map range i → replacement i (clamp to the last when counts differ);
      // replace last→first so earlier ranges keep their positions.
      for (let i = ranges.length - 1; i >= 0; i -= 1) {
        ranges[i].insertText(replacements[Math.min(i, replacements.length - 1)], 'Replace');
      }
      await context.sync();
      applied += Math.min(ranges.length, replacements.length);
    }

    return { applied, total: edits.length, failures };
  });
}

/**
 * Replace every occurrence of each find with the same replacement — the
 * write half of editing a detected field, where each find is one
 * whitespace-equivalence representative of the field's tag (the search
 * runs with `ignoreSpace`, so one representative covers `{{x}}` and
 * `{{ x }}` alike). Unlike `applyDocumentEdits`, every matched range gets
 * the replacement and `replaced` counts actual ranges. Finds longer than
 * 250 chars are reported as missing instead of searched — Word truncates
 * needles at 255, and replacing a truncated prefix would corrupt the tag.
 */
export async function replaceAllTagOccurrences(
  finds: string[],
  replacement: string
): Promise<{ replaced: number; missing: string[] }> {
  const wordApi = requireWordApi();
  return wordApi.run(async context => {
    let replaced = 0;
    const missing: string[] = [];
    for (const find of finds) {
      const needle = find.trim();
      if (!needle || needle.length > 250) {
        missing.push(find);
        continue;
      }
      const ranges = await searchAllRanges(context, needle);
      if (ranges.length === 0) {
        missing.push(find);
        continue;
      }
      // Replace last→first so earlier ranges keep their positions.
      for (let i = ranges.length - 1; i >= 0; i -= 1) {
        ranges[i].insertText(replacement, 'Replace');
      }
      await context.sync();
      replaced += ranges.length;
    }
    return { replaced, missing };
  });
}

/** Read the full document body text (for the chunks tool). */
export async function readDocumentBodyText(): Promise<string> {
  const wordApi = requireWordApi();
  return wordApi.run(async context => {
    const body = context.document.body;
    body.load('text');
    await context.sync();
    return body.text;
  });
}

export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = (): void => {
      reject(reader.error ?? new Error('Failed to read file.'));
    };
    reader.onload = (): void => {
      // readAsDataURL always yields a string result (never ArrayBuffer)
      const result = typeof reader.result === 'string' ? reader.result : '';
      const commaIndex = result.indexOf(',');
      resolve(commaIndex === -1 ? result : result.slice(commaIndex + 1));
    };
    reader.readAsDataURL(blob);
  });
}
